import { Clerk } from '@clerk/clerk-js';

let clerkInstance: Clerk | null = null;

export class AuthService {
    private static instance: AuthService;
    private clerk: Clerk | null = null;
    private isInitialized = false;
    private initializationPromise: Promise<void> | null = null;

    private constructor() {}

    static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    async initialize(): Promise<void> {
        if (this.isInitialized) return;

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = (async () => {
            const key = process.env.CLERK_PUBLISHABLE_KEY;
            console.log(`CLERK KEY: ${key}`);
            if (!key) {
                throw new Error('Missing CLERK_PUBLISHABLE_KEY. Set it in .env file.');
            }

            this.clerk = new Clerk(key);
            await this.clerk.load();

            if (this.clerk.session === undefined) {
                await new Promise<void>((resolve) => {
                    const unsubscribe = this.clerk!.addListener(({ session }) => {
                        if (session !== undefined) {
                            unsubscribe();
                            resolve();
                        }
                    });

                    setTimeout(() => {
                        unsubscribe();
                        resolve();
                    }, 2000);
                });
            }

            this.isInitialized = true;
        })();

        return this.initializationPromise;
    }

    isAuthenticated(): boolean {
        return this.clerk?.isSignedIn ?? false;
    }

    async getToken(): Promise<string | null> {
        return this.clerk?.session?.getToken() ?? null;
    }

    getUser() {
        return this.clerk?.user;
    }

    async signIn(email: string, password: string) {
        if (!this.clerk) throw new Error('Clerk not initialized');
        const signInAttempt = await this.clerk.client.signIn.create({
            strategy: 'password',
            identifier: email,
            password,
        });
        if (signInAttempt.status === 'complete') {
            await this.clerk.setActive({ session: signInAttempt.createdSessionId });
        }
        return signInAttempt;
    }

    async signUp(email: string, password: string) {
        if (!this.clerk) throw new Error('Clerk not initialized');
        const signUpAttempt = await this.clerk.client.signUp.create({
            emailAddress: email,
            password,
        });
        if (signUpAttempt.status === 'complete') {
            await this.clerk.setActive({ session: signUpAttempt.createdSessionId });
        }
        return signUpAttempt;
    }

    async signOut() {
        if (!this.clerk) throw new Error('Clerk not initialized');
        await this.clerk.signOut();
        localStorage.removeItem('clerk_session');
        window.location.hash = '#/';
    }

    onAuthStateChange(callback: (isAuthenticated: boolean) => void) {
        if (!this.clerk) return () => {};

        let previousAuthState = this.isAuthenticated();

        const unsubscribe = this.clerk.addListener((event) => {
            const currentAuthState = this.isAuthenticated();
            if (currentAuthState !== previousAuthState) {
                previousAuthState = currentAuthState;
                callback(currentAuthState);
            }
        });

        setTimeout(() => {
            const finalAuthState = this.isAuthenticated();
            if (finalAuthState !== previousAuthState) {
                callback(finalAuthState);
            }
        }, 100);

        return unsubscribe;
    }

    getClerk(): Clerk | null {
        return this.clerk;
    }
}
